import React from 'react';
import { remove } from 'api/GroupController';
import errorReporter from '@app/errorReporter';
import {
  Text,
  Dropdown,
  Menu,
  MenuItem,
  Button,
  Icon,
  message,
  TableProps,
  Space,
} from 'components';
import { useHistory } from 'react-router-dom';
import { AgentsContext, LayoutContext } from 'contexts';
import { StyledTable } from '../../../components';
import { addDatacy, addDatacyToHeader } from '../util';
import ManageUsersContext from '../ManageUsersContext';
import ManageUsersPagination from './ManageUsersPagination';
import { StyledSizeText } from './StyledComponents';
import TeamGroupRemovalConfirmation from './TeamGroupRemovalConfirmation';
import { TeamGroupModal } from '../TeamGroupModal';
import { TeamGroupModalContext } from '../TeamGroupModal/TeamGroupModalContext';
import useIsBranchAdmin from '../Hooks/useIsBranchAdmin';

const GroupsList: React.FC = () => {
  const history = useHistory();
  const { transition } = React.useContext(LayoutContext);
  const {
    isGroupsLoading: isLoading,
    groups,
    fetchGroups,
  } = React.useContext(ManageUsersContext);

  const { openModal } = React.useContext(TeamGroupModalContext);
  const { isBranchAdmin, branchAdminCanEdit } = useIsBranchAdmin();
  const { orgUsersMap, fetchOrgUsers } = React.useContext(AgentsContext);
  const [popconfirmVisibility, setPopconfirmVisibility] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    if (!orgUsersMap) fetchOrgUsers();
  }, [orgUsersMap, fetchOrgUsers]);

  const handleDelete = React.useCallback(
    async ({ id, name }: { id: string; name: string }) => {
      try {
        setIsDeleting(true);
        await remove(id);
        message.success(`${name} is now deleted`);
      } catch (err) {
        let msg = err.message;
        if (err.isAxiosError && err.response?.data?.message) {
          msg = err.response.data.message;
        }
        errorReporter.error(`Failed to remove group: ${msg}`);
        message.error(`Failed to remove group: ${msg}`);
      } finally {
        setIsDeleting(false);
        await fetchGroups();
      }
    },
    [],
  );

  React.useEffect(() => {
    const searchParams = new URLSearchParams(history.location.search);
    const groupIdToDelete = searchParams.get('deleteGroupId');
    if (groupIdToDelete) {
      const { id, name } =
        groups.find(group => group.id === groupIdToDelete) || {};
      if (id) {
        handleDelete({ id, name });
        searchParams.delete('deleteGroupId');
        history.replace({ search: searchParams.toString() });
      }
    }
  }, [handleDelete, history, groups]);

  const dataSource = React.useMemo(() => {
    const data = groups.map(group => ({
      id: group.id,
      name: group.name,
      users: group.users,
    }));
    return data;
  }, [groups]);

  const columns: TableProps['columns'] = [
    {
      width: 200,
      title: 'Group Name',
      key: 'groupName',
      dataIndex: 'name',
      sorter: (x, y) => {
        const a = x.name.toUpperCase();
        const b = y.name.toUpperCase();

        if (a === b) {
          return 0;
        }
        if (a > b) {
          return 1;
        }
        return -1;
      },
      render: (name, group) => {
        const { users } = group;
        let size = users.length;
        if (isBranchAdmin) {
          const mappedUsers = users.map((userId: any) => orgUsersMap?.[userId]);
          size = mappedUsers
            .filter(Boolean)
            .filter((user: any) => branchAdminCanEdit(user)).length;
        }
        return (
          <Space>
            <Text>{name}</Text>
            <StyledSizeText>
              {size} Member{size !== 1 ? 's' : ''}
            </StyledSizeText>
          </Space>
        );
      },
      onCell: addDatacy('groups-name'),
      onHeaderCell: addDatacyToHeader('groups-name'),
    },
    {
      width: 20,
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      align: 'center',
      render: (groupName, group) => {
        const { id, name } = group;
        return (
          <Dropdown
            placement="topLeft"
            trigger={['click']}
            dropdownRender={() => (
              <div onClick={e => e.stopPropagation()}>
                <TeamGroupRemovalConfirmation
                  title="Are you sure you’d like to delete this group?"
                  visible={popconfirmVisibility}
                  onCancel={() => setPopconfirmVisibility(false)}
                  handleDelete={() => handleDelete({ id, name })}
                >
                  <Menu>
                    <MenuItem
                      key={'edit'}
                      onClick={() => {
                        openModal('group', 'edit', id);
                      }}
                    >
                      Edit Group
                    </MenuItem>
                    <MenuItem
                      key={'delete'}
                      onClickCapture={e => {
                        setPopconfirmVisibility(true);
                        e.stopPropagation();
                      }}
                    >
                      Delete Group
                    </MenuItem>
                  </Menu>
                </TeamGroupRemovalConfirmation>
              </div>
            )}
          >
            <Button
              type="text"
              size="small"
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <Icon type="ellipsis" width={25} />
            </Button>
          </Dropdown>
        );
      },
      onCell: addDatacy('groups-dropdown'),
      onHeaderCell: addDatacyToHeader('groups-dropdown'),
    },
  ];

  const processedColumns = columns.filter(Boolean);

  return (
    <>
      <StyledTable
        style={{
          transition: `all ${transition}s`,
        }}
        pagination={false}
        showSorterTooltip={false}
        loading={isLoading || isDeleting}
        dataSource={dataSource}
        columns={processedColumns}
        scroll={{ x: '100%' }}
        tableLayout="fixed"
        onRow={group => ({
          onClick: () =>
            history.push(
              `/users/list/group/${group.id}${history.location.search}`,
            ),
        })}
        data-cy="groups-table"
      />
      <ManageUsersPagination hidden tableName="groups" />
      <TeamGroupModal destroyOnClose onSave={fetchGroups} />
    </>
  );
};

export default GroupsList;
